/**
 * Datenmodell des Roadbooks.
 *
 * Jede dieser Entitäten wird als eigenes Dokument gespeichert und
 * synchronisiert. Die Art (kind) und die ID bilden zusammen den Schlüssel,
 * lokal wie auf dem Server.
 */

/** Koordinate als [Breite, Länge], wie sie aus Kartendiensten kommt. */
export type LatLon = [number, number]

/** Charakter einer Etappe. Steuert nur die Darstellung. */
export type StageType = 'transit' | 'standtag' | 'pass' | 'kurz' | 'lang' | 'grenze' | 'heimweg'

export const STAGE_TYPES: StageType[] = [
  'transit',
  'standtag',
  'pass',
  'kurz',
  'lang',
  'grenze',
  'heimweg',
]

/** Ein Übernachtungsvorschlag. */
export interface Camping {
  name: string
  price: string
  note: string
  /**
   * Wo der Platz liegt, als [Breite, Länge].
   *
   * Optional, und das bleibt es: die meisten Einträge dieser Reise sind keine
   * Adresse, sondern eine Gegend („Camps um Ilidža“), und dafür gibt es keinen
   * Punkt, den man behaupten dürfte. Alte Bestände kennen das Feld ohnehin
   * nicht.
   *
   * Wo es fehlt, sitzt der Platz im Export für Navigationsgeräte weiterhin auf
   * dem Etappenziel, und die Beschreibung sagt das ausdrücklich (siehe
   * lib/navexport.ts). Wo es steht, führt das Gerät abends zum Platz und nicht
   * ins Ortszentrum - deshalb wird es nur gesetzt, wenn jemand einen Treffer
   * der Adresssuche ausgewählt oder die Zahlen selbst eingetragen hat. Ein
   * falsch gesetzter Platz ist schlimmer als gar keiner.
   */
  at?: LatLon
}

/**
 * Ein Zwischenpunkt einer Etappe: eine Stelle, über die die Strecke führen
 * muss.
 *
 * Er ist eine Vorgabe an die Routenberechnung und keine Auskunft über den
 * Weg - genau darin liegt sein Zweck. Eine Etappe hatte bis hierher nur Start
 * und Ziel, und der Routendienst nahm dazwischen, was er für schnell hielt:
 * Bled - Bovec fuhr er durch den Karawankentunnel und über den Predel, 108
 * statt 84 Kilometer und durch zwei andere Länder, während das Roadbook
 * derselben Etappe die vierundzwanzig Kehren des Vršič beschrieb. Die
 * gezeichnete Strecke widersprach damit dem eigenen Buch, und im Export fürs
 * Navigationsgerät führte sie am geplanten Höhepunkt vorbei.
 *
 * Der Name gehört dazu und ist nicht nur Zierde: in der Liste steht sonst ein
 * Zahlenpaar, und wer eine Etappe ein halbes Jahr später wieder aufmacht,
 * erkennt an „46.4337, 13.7469“ nicht den Pass. Er steht auch im Export für
 * Navigationsgeräte am Routenpunkt und wird dort unterwegs angesagt. Auf die
 * Strecke selbst hat er keinen Einfluss - wer ihn ändert, ändert die Fahrt
 * nicht, und die berechnete Strecke bleibt gültig (siehe routeState in
 * lib/route.ts).
 */
export interface Waypoint {
  /** Wie die Stelle heisst, etwa „Vršič-Pass“. Darf leer sein. */
  name: string
  /** Wo sie liegt, als [Breite, Länge]. */
  at: LatLon
}

/**
 * Die einmal berechnete Strasse einer Etappe.
 *
 * Sie liegt im Datenmodell und nicht in einem Zwischenspeicher: die App wird im
 * Funkloch benutzt, und eine Strecke, die unterwegs nachgefragt werden müsste,
 * wäre dort keine. So ist sie offline da, im Abgleich dabei und im Druck
 * verfügbar - und der Routendienst wird je Etappe genau einmal gefragt.
 *
 * from und to sind die Koordinaten, für die gerechnet wurde. Sie sind nicht
 * dasselbe wie die Enden der Geometrie: der Dienst setzt Start und Ziel auf die
 * nächste Strasse, das können einige hundert Meter sein. Nur mit den
 * angefragten Koordinaten lässt sich erkennen, dass jemand das Ziel seither
 * verschoben hat - und eine veraltete Strecke, die aussieht wie eine aktuelle,
 * ist schlechter als keine.
 */
/**
 * Eine Folge von Kehren auf der Strecke - eine Zeile im Roadbook, nicht eine je
 * Haarnadel.
 *
 * Der Bestand macht es vor: „24 Kehren hoch, 2. Gang, Kuehlwasser“ ist eine
 * Zeile. Fünfundzwanzig einzelne Zeichen wären dieselbe Auskunft auf zwei
 * Seiten, und im fahrenden Auto ist das keine Auskunft mehr.
 *
 * Was hier bewusst fehlt, ist die Richtung: hinauf oder hinunter. Die Höhen
 * stehen nirgends in dieser Antwort, und die Reihenfolge im Streckenverlauf
 * sagt es nicht - am Vršič liegen Auffahrt und Abfahrt hintereinander, und
 * welche von beiden die erste ist, hängt daran, von welcher Seite man kommt.
 * Eine geratene Richtung stünde im Buch neben einer gezählten Zahl und sähe
 * genauso verlässlich aus. Wer den Gang und das Kühlwasser dazuschreibt, weiss
 * ohnehin, in welche Richtung er fährt.
 */
export interface SerpentineRun {
  /** Kilometerstand der ersten Kehre, ab Beginn der Etappe. */
  km: number
  /** Wie viele Kehren die Folge hat. */
  count: number
  /** Wie lang die Folge ist, von der ersten bis zur letzten Kehre. */
  lengthKm: number
}

/**
 * Wie schwer ein Stück Strasse zu fahren ist.
 *
 * Vier Stufen und nicht zwei, und darin liegt der Sinn: zwischen einer
 * geschotterten Nebenstrasse, die jeder Kombi fährt, und einer Piste mit
 * `4wd_only` liegen Welten, und ein VW T3 liegt dazwischen. „Unbefestigt:
 * ja/nein“ würfe beides in einen Topf und wäre für die Entscheidung, um die es
 * geht, wertlos - man führe entweder zu viel aussen herum oder einmal zu weit
 * hinein.
 *
 * Die Einstufung trifft der Server (backend/internal/surface); dort steht auch,
 * aus welchen OSM-Merkmalen sie folgt. Hier steht sie, weil sie im Bestand
 * liegt und mitreist.
 */
export type SurfaceGrade = 'fest' | 'locker' | 'grob' | 'gelaende'

/**
 * Ein unbefestigter Abschnitt der Strecke.
 *
 * Ein Kilometerbereich und kein Warndreieck: „km 34 bis 41, 7 km Naturweg“ ist
 * eine Auskunft, mit der man planen kann, „Achtung Schotter“ ist keine.
 */
export interface SurfaceSection {
  /** Kilometerstand des Anfangs, ab Beginn der Etappe. */
  fromKm: number
  /** Kilometerstand des Endes. */
  toKm: number
  /** Länge in Metern. */
  lengthM: number
  /** Wie schwer dieser Abschnitt zu fahren ist. */
  grade: SurfaceGrade
  /**
   * Die rohen OSM-Merkmale des Weges, der den grössten Teil des Abschnitts
   * ausmacht - unverändert in der Schreibweise von OpenStreetMap.
   *
   * Sie stehen dabei, damit die Einstufung nachvollziehbar bleibt: „gelaende“
   * allein ist eine Behauptung, „gelaende, weil smoothness=horrible“ ist eine
   * Auskunft. Alle dürfen fehlen.
   */
  highway?: string
  surface?: string
  tracktype?: string
  smoothness?: string
  fourwd?: string
  /** Name und Nummer der Strasse. Entscheiden nichts, beschriften nur. */
  name?: string
  ref?: string
}

/**
 * Was über den Belag einer Strecke bekannt ist.
 *
 * Er liegt in der Etappe und nicht in einem Zwischenspeicher, aus demselben
 * Grund wie die Strecke selbst: die App wird im Funkloch benutzt, und eine
 * Auskunft, die unterwegs nachgefragt werden müsste, wäre dort keine.
 */
export interface SurfaceReport {
  /**
   * Anteil der Strecke, für den ein befahrener Weg gefunden wurde, 0 bis 1.
   *
   * Das Feld ist der Unterschied zwischen „hier ist alles asphaltiert“ und
   * „hier wurde nicht nachgesehen“, und ohne dieses Feld sähen beide gleich
   * aus. Eine leere Abschnittsliste bei niedriger Abdeckung ist keine
   * Entwarnung.
   */
  coverage: number
  /** Die unbefestigten Abschnitte in Fahrreihenfolge. */
  sections: SurfaceSection[]
  /** Summe der Abschnitte in Kilometern. */
  unpavedKm: number
  /** Zeitpunkt der Abfrage, Millisekunden seit 1970. */
  checkedAt: number
}

export interface StageRoute {
  /** Stützpunkte der Strasse als [Breite, Länge], Start zuerst. */
  geometry: LatLon[]
  /** Länge der Strecke laut Dienst. */
  distanceKm: number
  /** Fahrzeit laut Dienst, für einen gewöhnlichen Personenwagen. */
  durationH: number
  /** Zeitpunkt der Berechnung, Millisekunden seit 1970. */
  computedAt: number
  /**
   * Fassung des Verfahrens, mit dem die Stützpunkte ausgewählt wurden
   * (GEOMETRY_DETAIL in lib/route.ts).
   *
   * Fehlt in allem, was vor der Umstellung berechnet oder ausgeführt wurde;
   * das ist die erste Fassung mit ihren gleichmässigen Abständen. Nur daran
   * lässt sich eine grobe Strecke von einer genauen unterscheiden - beide sind
   * gültige Linien zwischen denselben Koordinaten.
   */
  detail?: number
  /**
   * Die Kehrenfolgen dieser Strecke, in Fahrreihenfolge.
   *
   * Gezählt auf der vollen Antwort des Dienstes, bevor die Stützpunkte
   * ausgedünnt werden (serpentineRuns in lib/route.ts). Danach ginge es nicht
   * mehr: von 3501 Punkten der Vršič-Etappe bleiben 400, und wo fünf eine
   * Haarnadel beschrieben haben, steht dann einer.
   *
   * Fehlt in allem, was vor detail 3 berechnet wurde, und auch in jeder
   * Strecke ohne Kehren. Beides ist dasselbe für die Anzeige - „keine Zeile“ -
   * und etwas anderes für die Frage, ob nachgezählt werden muss; die
   * beantwortet detail.
   */
  serpentines?: SerpentineRun[]
  /**
   * Was über den Belag dieser Strecke bekannt ist.
   *
   * Fehlt, solange niemand gefragt hat - und das ist etwas anderes als eine
   * leere Abschnittsliste. „Noch nicht nachgesehen“ und „nachgesehen und
   * durchgehend befestigt“ dürfen in der Anzeige nie gleich aussehen.
   *
   * Gefragt wird auf Knopfdruck und nicht beim Berechnen der Strecke: die
   * Auskunft kommt aus OpenStreetMap über einen Dienst, der langsam und
   * unzuverlässig ist, und eine Routenberechnung darf daran nicht
   * hängenbleiben. Was einmal geholt wurde, liegt danach hier und ist
   * unterwegs ohne Netz da.
   *
   * Sie hängt an der berechneten Strecke: wer Start, Ziel oder Zwischenpunkte
   * ändert, bekommt eine neue Strecke, und mit ihr fällt diese Auskunft weg.
   * Ein Belag, der zu einer anderen Strasse gehört, sähe genauso aus wie ein
   * richtiger.
   */
  surface?: SurfaceReport
  /** Startkoordinate, für die gerechnet wurde. */
  from: LatLon
  /**
   * Die Zwischenpunkte, über die gerechnet wurde - nur ihre Koordinaten, in
   * Fahrreihenfolge.
   *
   * Aus demselben Grund wie from und to: nur so lässt sich erkennen, dass
   * jemand die Liste seither geändert hat. Eine Strecke, die den Pass nicht
   * mehr nimmt, obwohl er in der Etappe steht, ist eine veraltete Strecke, die
   * aussieht wie eine aktuelle - und das ist schlechter als keine.
   *
   * Nur die Koordinaten und nicht die Namen: der Name bestimmt keinen Weg. Wer
   * „Vršič“ in „Vršič-Pass“ umbenennt, hat nichts an der Fahrt geändert, und
   * eine Neuberechnung deswegen wäre eine Anfrage an einen fremden Dienst für
   * nichts.
   *
   * Fehlt in allem, was vor dieser Erweiterung berechnet oder importiert
   * wurde. Das ist gleichbedeutend mit „ohne Zwischenpunkte gerechnet“ - denn
   * genau das war es.
   */
  via?: LatLon[]
  /** Zielkoordinate, für die gerechnet wurde. */
  to: LatLon
}

/**
 * Die Form einer Tulpe: was an dieser Stelle steht.
 *
 * Die Auswahl ist bewusst klein - siehe die Begründung je Form in lib/tulip.ts.
 * Dreizehn davon sind an der ersten Reise gemessen (Schweiz - Balkan -
 * Schwarzes Meer); eine Form, die dort nicht vorkam, hätte im fahrenden Auto
 * niemandem geholfen und die Liste, aus der die Beifahrerin wählen muss, nur
 * länger gemacht.
 *
 * Zwei stehen aus einem anderen Grund da. Fähre und Schotter hängen an keiner
 * Strecke: die eine ist überall dort, wo eine Küste ist, der andere überall
 * dort, wo der Belag aufhört. Sie sind der Massstab dafür, wann eine weitere
 * Form dazugehört - nicht "kommt auf dieser Reise vor", sondern "kommt auf
 * jeder vor, und ohne das Zeichen steht die Auskunft in keiner Zeile".
 */
export type TulipShape =
  | 'kreisel'
  | 'kreuzung'
  | 't'
  | 'gabel'
  | 'abzweig'
  | 'auffahrt'
  | 'abfahrt'
  | 'grenze'
  | 'maut'
  | 'faehre'
  | 'tunnel'
  | 'serpentine'
  | 'schotter'
  | 'bahn'
  | 'ort'

/**
 * Eine Tulpe: eine Stelle der Etappe als Skizze statt als Satz.
 *
 * Die Notation kommt aus dem gedruckten Rallye-Roadbook. Ein Kreis mit einem
 * Punkt - der Punkt ist, wo man herkommt - und davon abgehend die Wege, die es
 * an dieser Stelle gibt; der zu fahrende trägt den Pfeil. Wer die Skizze im
 * Auto ansieht, weiss in einer Sekunde, wo er abbiegen muss, ohne einen Satz zu
 * lesen.
 *
 * Die Reihenfolge in der Liste ist die Reihenfolge der Fahrt und wird vom
 * Nutzer bestimmt, nicht aus km gerechnet: unterwegs wird eine Zeile
 * nachgetragen, deren Kilometerstand noch niemand kennt, und eine Liste, die
 * sich beim Tippen selbst umsortiert, verliert man aus den Augen.
 */
export interface Tulip {
  /**
   * Kilometerstand ab Beginn der Etappe.
   *
   * NaN heisst "noch nicht bekannt" und ist ein völlig normaler Zustand: die
   * Zeile wird unterwegs notiert, bevor jemand den Tacho abgelesen hat. Im Buch
   * steht dann ein Strich. Eine 0 wäre hier keine Antwort, sondern eine
   * falsche - sie sagt "gleich am Anfang der Etappe".
   */
  km: number
  shape: TulipShape
  /**
   * Wohin es weitergeht, in Grad im Uhrzeigersinn: 0 geradeaus, 90 rechts,
   * 270 links. Formen ohne Wahl (Tunnel, Grenze) lassen den Wert liegen.
   */
  turn: number
  /** Der kurze Hinweis rechts neben der Skizze. */
  note: string
  /**
   * Die Vorschlagsstelle, aus der diese Tulpe übernommen wurde.
   *
   * Der Schlüssel aus lib/tulipMerge.ts, etwa "kreisel@3.7": die Form und der
   * Kilometerstand des Vorschlags, wie er im Moment der Übernahme dastand.
   *
   * Fehlt bei jeder von Hand eingetragenen Tulpe, und das ist die eigentliche
   * Auskunft dieses Feldes - es unterscheidet "kommt aus einem Vorschlag" von
   * "hat jemand selbst geschrieben". Daran hängen zwei Dinge: ein zweiter
   * Vorschlagslauf erkennt daran, dass diese Zeile schon im Buch steht (und
   * bietet sie nicht ein zweites Mal an), und wer sie löscht, sagt damit Nein
   * zu einem Vorschlag - beim Löschen einer selbst geschriebenen Zeile ist
   * nichts abgelehnt worden, denn vorgeschlagen war sie nie.
   *
   * Der Kilometerstand darin ist der des Vorschlags und wird nie
   * mitgeschrieben, wenn jemand km von Hand verschiebt: gemeint ist die Stelle,
   * an der der Vorschlag stand, und nur so bleibt eine berichtigte Zeile
   * wiedererkennbar. Gedruckt wird er nirgends.
   */
  src?: string
}

/** Eine Tagesetappe. */
export interface Stage {
  id: string
  /**
   * Position in der Reihenfolge, beginnend bei 1. Wird beim Umsortieren neu
   * vergeben.
   *
   * Trotz des Namens keine Tagesnummer und nirgends eine Beschriftung: die
   * ausgelassenen Etappen zählen hier mit, ein Reisetag entsteht daraus also
   * nicht. Was in der App oder im Buch als „Tag“ dasteht, kommt aus
   * lib/calendar.ts.
   */
  day: number
  from: string
  to: string
  /** Startkoordinate. */
  c1: LatLon
  /** Zielkoordinate. */
  c2: LatLon
  /**
   * Stellen, über die die Strecke dieser Etappe führen muss, in
   * Fahrreihenfolge.
   *
   * Optional, weil die allermeisten Etappen keine brauchen: von Zadar nach
   * Bihać gibt es einen vernünftigen Weg, und den findet der Dienst allein.
   * Gebraucht werden sie dort, wo die Strasse selbst das Ziel ist - der Vršič,
   * der Transfăgărășan, die Transalpina, die M25 durch die Đerdap-Schlucht -,
   * denn dort ist die schnellste Verbindung nicht die gemeinte. Ältere Dateien
   * kennen das Feld nicht, und eine Etappe ohne dieses Feld verhält sich
   * genauso wie vor seiner Einführung.
   *
   * Die Reihenfolge ist die der Fahrt und wird vom Nutzer bestimmt. Sortiert
   * wird nichts: erst über den Pass und dann ins Tal ist eine andere Fahrt als
   * umgekehrt.
   */
  via?: Waypoint[]
  km: number
  /** Reine Fahrzeit in Stunden. */
  h: number
  /** Länderkürzel oder Übergang, etwa "SI" oder "HR→BA". */
  land: string
  type: StageType
  camping: Camping[]
  highlights: string[]
  night: string[]
  tip: string
  /**
   * Die berechnete Strasse dieser Etappe, sofern sie schon geholt wurde.
   *
   * Optional, weil eine frisch angelegte Etappe keine hat und der Import alter
   * Dateien keine mitbringt. Wo sie fehlt, zeichnet die Karte die Luftlinie.
   */
  route?: StageRoute
  /**
   * Die Tulpen dieser Etappe, in Fahrreihenfolge.
   *
   * Optional, weil die allermeisten Etappen keine haben: eine Autobahnetappe
   * quer durch Ungarn braucht keine Skizze, der Vrsic-Pass und die M25 durch
   * die Djerdap-Schlucht schon. Ältere Dateien kennen das Feld nicht.
   */
  tulips?: Tulip[]
  /**
   * Die Vorschlagsstellen, zu denen jemand Nein gesagt hat.
   *
   * Dieselben Schlüssel wie in Tulip.src (lib/tulipMerge.ts), etwa
   * "schotter@41.2". Hineingeschrieben wird eine Stelle, wenn ein Vorschlag
   * abgewählt oder eine daraus übernommene Tulpe gelöscht wird; herausgenommen,
   * wenn jemand den Haken wieder setzt oder das Gelöschte zurückholt.
   *
   * Am Dokument und nicht in der Maske, denn ein Nein muss zweierlei
   * überstehen: den nächsten Druck auf „Tulpen vorschlagen“ - die Vorschläge
   * werden nach jeder Streckenänderung und nach jedem Belagsabruf neu geholt -
   * und den Abgleich mit dem zweiten Gerät. Eine Reise wird zu zweit geplant;
   * wenn der eine ein Zeichen verwirft und der andere es tags darauf wieder
   * angehakt vorfindet, ist nichts gewonnen. Genau daran hängt der Schaden:
   * eine Zeile, die man begründet weggeworfen hat und die wieder angehakt
   * dasteht, wird irgendwann versehentlich übernommen - und dieselbe Anweisung
   * zweimal im gedruckten Buch ist bei Tempo 80 schlimmer als eine fehlende.
   *
   * Optional und in älteren Dateien nicht vorhanden. Eine Etappe ohne dieses
   * Feld verhält sich wie vor seiner Einführung: es wurde noch nie etwas
   * abgelehnt.
   */
  tulipsRejected?: string[]
  /** Puffertag: zählt mit, ist aber als verschiebbar markiert. */
  optional?: boolean
  /** Ausgelassen: bleibt sichtbar, fällt aus allen Summen heraus. */
  skip?: boolean
}

/** Rahmendaten der Reise. */
export interface TripMeta {
  title: string
  /**
   * Der Name des Fahrzeugs.
   *
   * Die Angabe aus der Zeit, in der das Fahrzeug nur ein Name war. Sie bleibt
   * und wird weiter mitgeschrieben (siehe mitFahrzeug in lib/vehicle.ts):
   * Dokumente aus jener Zeit tragen hier den echten Namen, und eine ältere
   * Fassung der App - ein zweites Gerät, das noch den vorigen Stand im Cache
   * hat - liest ihn weiterhin von hier.
   *
   * Gelesen wird sie nicht mehr unmittelbar: das Fahrzeug steht in
   * Trip.vehicle, und wer den Namen braucht, holt ihn über fahrzeug() dort ab.
   */
  vehicle: string
  crew: number
  /** Startdatum als ISO-Datum, YYYY-MM-DD. */
  start: string
  /**
   * Verbrauch in Litern je 100 km.
   *
   * Dieselbe Geschichte wie beim Namen darüber: der Verbrauch gehört seit der
   * Einführung des Fahrzeugs dorthin und wird hier nur noch mitgeschrieben,
   * damit eine ältere Fassung der App die Kraftstoffschätzung nicht auf null
   * fallen lässt.
   */
  consumption: number
  /**
   * Angenommener Kraftstoffpreis je Liter - beim Strom je Kilowattstunde.
   *
   * Eine Angabe der Reise und keine des Fahrzeugs: derselbe Wagen tankt 2026
   * anders als 2019 und auf dem Balkan anders als daheim.
   */
  fuelPrice: number
  /**
   * Währung der Budgetanzeige, als Code nach ISO 4217 („EUR“, „CHF“).
   *
   * Ebenfalls eine Angabe der Reise: gefahren wird mit demselben Fahrzeug
   * durch Länder, die verschieden zahlen. Änderbar im Reiter Daten - vorher
   * stand das Feld zwar im Modell, aber in keinem Formular, und eine Schweizer
   * Reise zeigte überall €.
   */
  currency: string
}

export interface Trip {
  meta: TripMeta
  /**
   * Womit gefahren wird.
   *
   * Am Reisedokument und nicht in einem geräteweiten Fuhrpark: das Buch wird
   * gedruckt und mit den Mitreisenden geteilt, und dort muss stehen, womit
   * tatsächlich gefahren wurde - nicht, was auf dem Gerät des Druckenden
   * zuletzt eingestellt war. So reist das Fahrzeug durch den Abgleich, in die
   * Exportdatei und auf jedes Gerät, das die Reise sieht.
   *
   * Optional, weil alles, was vor seiner Einführung geschrieben wurde, keines
   * hat. Dort steht die Angabe verstreut in meta.vehicle (Name) und
   * meta.consumption (Verbrauch), während Kraftstoffart, Tank, Zeitzuschlag
   * und Nationalitätszeichen fest verdrahtet im Programm standen: ein
   * Benziner, 25 Prozent Zuschlag, CH. Genau das setzt fahrzeug() ein, solange
   * das Feld fehlt - siehe lib/vehicle.ts.
   */
  vehicle?: Vehicle
  stages: Stage[]
}

/**
 * Was ein Fahrzeug tankt oder lädt.
 *
 * Vier Arten, und die Auswahl ist bewusst klein. Sie entscheidet zweierlei und
 * sonst nichts: wie die Kraftstoffkategorie im Budget heisst (ein Diesel bucht
 * nicht unter „Benzin“) und in welcher Einheit Verbrauch und Tankinhalt stehen.
 * Alles, was darüber hinausginge - Euro-Norm, Zylinderzahl, Ladeleistung -,
 * stünde im Modell, ohne irgendwo hineinzuwirken.
 */
export type FuelKind = 'benzin' | 'diesel' | 'gas' | 'strom'

export const FUEL_KINDS: FuelKind[] = ['benzin', 'diesel', 'gas', 'strom']

/**
 * Das Fahrzeug einer Reise.
 *
 * Hier steht, was die App über das Fahrzeug wissen muss, um zu rechnen und zu
 * drucken - und nichts weiter. Jedes Feld hat genau eine Stelle, an der es
 * wirkt; was keine hätte, steht nicht hier (siehe FuelKind).
 */
export interface Vehicle {
  /** Wie es heisst, etwa „VW T3 Camper“. Leer heisst: noch nicht eingetragen. */
  name: string
  fuel: FuelKind
  /** Verbrauch je 100 km, in Litern - beim Strom in Kilowattstunden. */
  consumption: number
  /**
   * Tankinhalt in Litern, beim Strom die Kapazität in Kilowattstunden.
   *
   * 0 heisst „nicht eingetragen“ und ist der Normalfall. Steht er da, ergibt
   * er zusammen mit dem Verbrauch die Reichweite - auf dem Balkan zwischen
   * zwei Tankstellen eine echte Frage, und keine, die sich unterwegs im Kopf
   * rechnen lässt.
   */
  tank: number
  /**
   * Zuschlag auf die Fahrzeit des Routendienstes, als Anteil: 0.25 sind 25
   * Prozent, 0 ist keiner.
   *
   * Der Dienst rechnet mit einem gewöhnlichen Personenwagen. Ein beladener
   * Camper ist langsamer, ein Personenwagen ist es nicht - deshalb steht die
   * Zahl am Fahrzeug und nicht als Konstante im Programm. Sie bleibt eine
   * Schätzung: übernommen wird sie nur auf Knopfdruck, und die Oberfläche
   * zeigt beide Zeiten nebeneinander.
   */
  surcharge: number
  /**
   * Nationalitätszeichen, etwa „CH“ - was auf dem ovalen Schild steht.
   *
   * Es steht im Kopf der App und auf dem Buchrücken. Leer heisst: kein Schild,
   * und dann steht dort auch keines. Vorher stand dort für jede Reise „CH“,
   * fest im JSX.
   */
  sign: string
}

/** Ein Halt aus der Aufzeichnung. */
export interface TrackStop {
  /** Beginn, Millisekunden seit 1970. */
  from: number
  /** Ende, Millisekunden seit 1970. */
  to: number
  /** Wo gestanden wurde: Mittel der Punkte, die zu diesem Halt gehören. */
  at: LatLon
}

/**
 * Ein Ort mit seiner Uhrzeit: wo die Reisenden zu diesem Zeitpunkt waren.
 *
 * Der Weg (geometry) weiss, wo es langging, aber nicht wann - beim Ausdünnen
 * fallen die Zeitstempel weg, und mehr als Anfang und Ende der Fahrt bliebe
 * sonst nicht. Ein paar echte Punkte mit ihrer echten Uhrzeit sind das, was der
 * Wettervorschlag braucht (siehe lib/weather.ts): das Wetter eines Tages ist
 * nicht der Wert an einem Punkt, sondern der Verlauf entlang der Fahrt.
 *
 * Wenige und echte Punkte, nicht viele gerechnete: eine Stelle zwischen zwei
 * Aufzeichnungspunkten liesse sich interpolieren, wäre dann aber eine Annahme
 * über die Fahrt und keine Beobachtung mehr - und genau daran hängt, dass der
 * abgeleitete Wert ein Tatsachenanker bleibt.
 */
export interface TrackFix {
  /** Wo, als [Breite, Länge]. */
  at: LatLon
  /** Wann, Millisekunden seit 1970. */
  t: number
}

/** Ein Punkt des Höhenprofils: [Kilometer ab Start, Höhe in Metern]. */
export type TrackProfilePoint = [number, number]

/** Das Höhenprofil einer Aufzeichnung, geglättet. */
export interface TrackElevation {
  /** Verlauf über die gefahrene Strecke, ausgedünnt. */
  profile: TrackProfilePoint[]
  /** Tiefster Punkt in Metern. */
  min: number
  /** Höchster Punkt in Metern. */
  max: number
  /** Summe des Anstiegs in Metern, nach Glättung und mit Schwelle. */
  gain: number
}

/**
 * Der aufgezeichnete Weg einer Etappe, aus einer GPX-Datei.
 *
 * Er hängt am Tagebuch und nicht an der Etappe, weil er nicht zur Planung
 * gehört, sondern zu dem, was tatsächlich passiert ist: dieselbe Etappe kann
 * geplant sein, ohne je gefahren zu werden, und der Track entsteht erst
 * unterwegs. Eine eigene Dokumentart wäre er nur um den Preis einer zweiten
 * Kennung, eigener Grabsteine und eines eigenen Platzes im Austauschformat -
 * für etwas, das genau einen Besitzer hat und mit ihm entsteht und vergeht.
 *
 * Die Rohpunkte einer Aufzeichnung sind Tausende, und das Tagebuchdokument geht
 * bei jeder Änderung als Ganzes durch den Abgleich. Deshalb steht hier die
 * ausgedünnte Fassung des Wegs (siehe MAX_TRACK_POINTS in lib/gpx.ts) und
 * daneben das fertig Gerechnete - gerechnet wurde über alle Rohpunkte, einmal
 * beim Import. Die Anzeige liest nur noch ab.
 */
export interface DiaryTrack {
  /** Stützpunkte des Wegs als [Breite, Länge], ausgedünnt. */
  geometry: LatLon[]
  /**
   * Die einzelnen Dateien, aus denen dieser Weg zusammengesetzt ist.
   *
   * Fehlt, solange nur eine Datei eingelesen wurde - dann ist der Track selbst
   * schon dieser eine Teil, und ein Feld mit einer Kopie seiner selbst wäre
   * doppelt gespeichert, doppelt abgeglichen und die häufigste Form eines
   * Widerspruchs. Ab der zweiten Datei stehen hier alle Teile in zeitlicher
   * Reihenfolge, und alles darüber (Kilometer, Zeiten, Halte, Profil, Weg) ist
   * aus ihnen gerechnet - siehe mergeTrackParts in lib/gpx.ts.
   *
   * Warum die Teile überhaupt liegen bleiben und nicht nur ihre Summe: das
   * Gerät geht nach fünf Minuten Stillstand in Bereitschaft, ein Mittagshalt
   * schneidet die Aufzeichnung also in zwei Dateien, und wer die zweite
   * einliest, will die erste behalten. Und wer die falsche einliest, muss genau
   * sie wieder herausnehmen können - ohne die richtige noch einmal zu suchen.
   * Das geht nur, wenn jeder Teil seine eigenen Zahlen behält.
   */
  parts?: TrackPart[]
  /**
   * Fingerabdruck der Rohpunkte, aus denen dieser Weg entstanden ist.
   *
   * Nur bei einem Track aus genau einer Datei gesetzt; ein zusammengesetzter
   * trägt ihn je Teil. Fehlt bei Tracks aus der Zeit vor dem Feld - dann
   * entscheidet allein der Zeitbereich, ob eine Datei schon einmal da war.
   */
  fingerprint?: string
  /** Tatsächlich gefahrene Strecke, Summe aller Abstände. */
  distanceKm: number
  /** Erster Zeitstempel, oder null wenn die Datei keine mitbringt. */
  startedAt: number | null
  /** Letzter Zeitstempel, oder null. */
  endedAt: number | null
  /** Gesamtzeit in Sekunden: letzter minus erster Zeitstempel. */
  totalSeconds: number
  /**
   * Fahrzeit in Sekunden: die Gesamtzeit ohne die erkannten Halte und ohne die
   * Zeit, in der gar nicht aufgezeichnet wurde.
   *
   * Nur gemessene Fahrt. Was zwischen zwei Dateien liegt, steht in
   * unrecordedSeconds und zählt hier nicht mit - eine Datei, die um 10:12
   * aufhört und die nächste, die um 12:02 anfängt, sagen über die 110 Minuten
   * dazwischen nichts.
   */
  movingSeconds: number
  /** Standzeit in Sekunden: Summe der erkannten Halte. */
  stoppedSeconds: number
  /**
   * Zeit in Sekunden, in der nichts aufgezeichnet wurde.
   *
   * Die Lücken zwischen den Teilen, in denen das Fahrzeug nicht gestanden hat -
   * stand es, ist die Lücke ein Halt und steht in stoppedSeconds (siehe
   * brueckeZwischen in lib/gpx.ts). Bleibt der andere Fall: das Gerät war aus
   * und der Wagen woanders, als er wieder anging.
   *
   * Was in dieser Zeit passiert ist, weiss keine Datei. Sie als Fahrzeit zu
   * buchen behauptet eine Messung, die es nicht gibt, und macht aus zwei
   * gefahrenen Stunden vier; sie als Standzeit zu buchen behauptet das
   * Gegenteil, obwohl der Wagen nachweislich woanders wieder auftaucht. Sie ist
   * deshalb eine eigene Grösse und geht in keine der beiden ein.
   *
   * Fehlt, wo es sie nicht gibt - also bei jedem Track aus einer einzigen
   * Datei, und das ist der Normalfall. Die drei Zeiten ergeben zusammen die
   * Gesamtzeit: totalSeconds = movingSeconds + stoppedSeconds +
   * unrecordedSeconds.
   */
  unrecordedSeconds?: number
  /** Die Halte in ihrer zeitlichen Reihenfolge. */
  stops: TrackStop[]
  /**
   * Wo die Reisenden wann waren, in wenigen echten Punkten - höchstens einer je
   * Stunde (MAX_TRACK_FIXES in lib/gpx.ts).
   *
   * Optional, weil Aufzeichnungen ohne Zeitstempel vorkommen und weil Tracks
   * aus der Zeit vor dem Feld sie nicht haben. Ohne sie gibt es keinen
   * Wettervorschlag, und das ist richtig so: ohne Uhr weiss niemand, wann die
   * Fahrt war.
   */
  fixes?: TrackFix[]
  /** Höhenprofil, sofern die Datei Höhen enthielt. */
  elevation?: TrackElevation
  /** Zahl der Rohpunkte, aus denen gerechnet wurde. */
  pointCount: number
  /** Zahl der Segmente (trkseg), die zusammengelegt wurden. */
  segments: number
  /** Punkte, die beim Lesen als unbrauchbar übersprungen wurden. */
  dropped: number
  /** Name der Datei, aus der der Track kam. */
  fileName: string
  /** Zeitpunkt des Imports, Millisekunden seit 1970. */
  importedAt: number
}

/**
 * Ein Teil eines zusammengesetzten Wegs: was aus genau einer Datei kam.
 *
 * Dasselbe wie ein Track aus einer einzelnen Datei - ohne die Teileliste, die
 * es nur auf der obersten Ebene gibt, und mit einem Fingerabdruck, der hier
 * nicht fehlen darf: an ihm erkennt der Import, dass dieselbe Aufzeichnung
 * schon einmal eingelesen wurde. Leer heisst "unbekannt" und kommt aus alten
 * Dateien.
 *
 * Die Zahlen eines Teils sind die seiner eigenen Fahrt und werden nie
 * nachgerechnet: gerechnet wurde einmal beim Import über alle Rohpunkte, und
 * die gibt es danach nicht mehr. Nur die Stützpunkte werden beim
 * Zusammenlegen weiter ausgedünnt, damit die Summe aller Teile den Rahmen von
 * MAX_TRACK_POINTS nicht sprengt.
 */
export type TrackPart = Omit<DiaryTrack, 'parts' | 'fingerprint'> & { fingerprint: string }

/**
 * Ein Stundenwert des Wetters, an einem Ort der Fahrt zu seiner Stunde.
 *
 * Die drei Messwerte kommen unverändert vom Dienst. Was daraus für ein Satz
 * wird, entscheidet lib/weather.ts - genau wie bei der Strecke, wo die
 * Fahrzeit des Dienstes im Modell liegt und der Zuschlag erst im Vorschlag
 * entsteht.
 */
export interface WeatherHour {
  /** Zeitpunkt in Millisekunden seit 1970, UTC. */
  at: number
  /**
   * Versatz der Ortszeit gegen UTC in Sekunden.
   *
   * Der Satz, der daraus entsteht, handelt von der Ortszeit: „ab Mittag“ meint
   * Mittag dort, wo die Reisenden waren, und nicht Mittag in der Zeitzone des
   * Geräts, auf dem die Datei eingelesen wurde.
   */
  offsetSeconds: number
  /** Temperatur in Grad Celsius. */
  tempC: number
  /** Wettercode der WMO: 0 wolkenlos, 61 Regen, 95 Gewitter und so fort. */
  code: number
  /** Windgeschwindigkeit in km/h. */
  windKmh: number
}

/**
 * Was der Wetterdienst zur aufgezeichneten Fahrt einer Etappe geliefert hat.
 *
 * Sie liegt im Datenmodell und nicht in einem Zwischenspeicher - dieselbe
 * Überlegung wie bei StageRoute: die App wird im Funkloch benutzt, und ein
 * Wert, der unterwegs nachgefragt werden müsste, wäre dort keiner.
 *
 * startedAt und endedAt sind der Zeitbereich, für den geholt wurde. Sie stehen
 * hier aus demselben Grund, aus dem StageRoute ihre from- und to-Koordinaten
 * mitführt: kommt eine zweite GPX-Datei dazu, ist der Tag ein anderer, und eine
 * Auskunft über die halbe Fahrt, die aussieht wie eine über die ganze, ist
 * schlechter als keine.
 *
 * Was daraus im Tagebuch steht, entscheidet der Nutzer. Dieses Feld füllt
 * DiaryEntry.weather nicht auf - es schlägt einen Inhalt vor.
 */
export interface DiaryWeather {
  /** Die Stundenwerte entlang der Fahrt, in zeitlicher Reihenfolge. */
  hours: WeatherHour[]
  /** Zeitpunkt des Abrufs, Millisekunden seit 1970. */
  fetchedAt: number
  /** Der Dienst, etwa „open-meteo“. */
  source: string
  /**
   * Die Namensnennung, die die Lizenz des Dienstes verlangt.
   *
   * Sie steht im Dokument und nicht als Konstante in der Ansicht: die Werte
   * bleiben Jahre liegen, gehen durch den Abgleich und in die Exportdatei, und
   * die Nennung gehört zu ihnen. Ein Wechsel des Dienstes darf die Nennung der
   * alten Daten nicht überschreiben.
   */
  attribution: string
  /** Erster Zeitstempel der Fahrt, für die geholt wurde. */
  startedAt: number
  /** Letzter Zeitstempel der Fahrt, für die geholt wurde. */
  endedAt: number
}

/**
 * Was unterwegs tatsächlich passiert ist, je Etappe.
 *
 * Hier stehen nur noch die Tatsachen des Tages: Haken, Datum, Tachostand,
 * gefahrene Kilometer, Wetter und der aufgezeichnete Weg. Sie gehören genau
 * einmal hin, egal wer sie einträgt - zwei Leute, die denselben Tachostand
 * ablesen, lesen denselben ab.
 *
 * Was jemand über den Tag schreibt, steht dagegen nicht mehr hier, sondern in
 * eigenen Dokumenten (DiaryPost). Der Grund steht dort.
 */
export interface DiaryEntry {
  stageId: string
  done: boolean
  /** ISO-Datum, YYYY-MM-DD. */
  date: string
  /** Tachostand bei Ankunft. */
  odo: string
  /** Tatsächlich gefahrene Kilometer, falls sie von der Planung abweichen. */
  realKm: string
  /**
   * Das Wetter des Tages, als freier Text.
   *
   * Was hier steht, hat ein Mensch geschrieben oder ausdrücklich übernommen.
   * Der Vorschlag aus der Aufzeichnung (weatherFromTrack) schreibt hier nie von
   * selbst hinein - er bleibt als Vorschlag erkennbar, bis jemand drückt.
   */
  weather: string
  /**
   * Der Tagebuchtext aus der Zeit vor den Beiträgen.
   *
   * Bleibt im Modell, weil er auf der laufenden Installation echten Inhalt
   * trägt und jederzeit über den Abgleich hereinkommen kann - von einem Gerät,
   * das lange nicht online war. Gelesen wird er in lib/diary.ts, wo er zu einem
   * Beitrag wird; geschrieben wird er nirgends mehr. Ist er leer, ist die
   * Überführung für diese Etappe erledigt.
   */
  text: string
  /**
   * Der aufgezeichnete Weg dieser Etappe, sofern eine GPX-Datei importiert
   * wurde.
   *
   * Optional, weil die allermeisten Einträge keinen haben: aufgezeichnet wird,
   * wer mag, und ältere Dateien kennen das Feld nicht.
   */
  track?: DiaryTrack
  /**
   * Die Stundenwerte des Wetters entlang dieser Fahrt, sofern sie geholt
   * wurden.
   *
   * Es gibt sie nur mit Aufzeichnung, und das ist die Regel dieser Funktion und
   * keine technische Grenze: aus Zielkoordinate und geratener Ankunftszeit käme
   * eine plausible Zahl heraus, die aussieht wie eine Messung - und eine
   * plausible Zahl, die aussieht wie eine Messung, ist schlimmer als gar keine.
   * Wer nicht aufgezeichnet hat, schreibt das Wetter selbst hin; er war ja
   * dabei.
   *
   * Getrennt von weather und nicht darin: das eine sind die Werte, das andere
   * ist der Satz, den ein Mensch verantwortet. Ein Vorschlag überschreibt
   * niemals, was jemand eingetragen hat.
   */
  weatherFromTrack?: DiaryWeather
}

/**
 * Ein Beitrag im Tagebuch: was eine Person zu einer Etappe geschrieben hat.
 *
 * Eine eigene Dokumentart und kein Feld am Tagebucheintrag - genauso wie beim
 * Foto, und aus demselben Grund. Ein Dokument geht als Ganzes durch den
 * Abgleich, und ein Konflikt entscheidet nach Zeitstempel für eine der beiden
 * Fassungen. Bei einem gemeinsamen Textfeld hiesse das: wer abends als zweiter
 * tippt, löscht den Abend des anderen, und zwar auf allen Geräten. Schreiben
 * beide dagegen je einen Beitrag, entstehen zwei Dokumente, die einander nie
 * begegnen - der Konflikt kommt gar nicht erst zustande. Das ist die Lösung,
 * nicht eine hübschere Konfliktbehandlung.
 *
 * Was Tatsache der Etappe ist, bleibt am Tagebucheintrag (siehe DiaryEntry).
 */
export interface DiaryPost {
  id: string
  stageId: string
  /**
   * Kennung des Verfassers: der sub-Claim aus dem Token.
   *
   * Die Kennung und nicht der Name. Namen ändern sich - Heirat, Schreibweise,
   * ein zweites Konto -, und wem ein Beitrag gehört, darf davon nicht abhängen.
   * Leer heisst "unbekannt": so sehen Beiträge aus, die aus einer Datei kommen,
   * deren Fassung das Feld noch nicht kannte.
   */
  authorId: string
  /**
   * Anzeigename zum Zeitpunkt des Schreibens.
   *
   * Eine Kopie und kein Verweis, denn es gibt nichts, worauf man verweisen
   * könnte: der Name des anderen steht in keinem Dokument dieser Reise, er käme
   * allein aus dessen Token. Ohne die Kopie stünde im Buch "Verfasser
   * unbekannt", sobald der zweite gerade nicht angemeldet ist. Dass hier der
   * Stand von damals steht, ist dabei richtig und nicht ungenau - im Buch
   * steht, wer den Abend geschrieben hat, nicht wie diese Person heute heisst.
   */
  authorName: string
  /** Zeitpunkt des Anlegens, Millisekunden seit 1970. */
  createdAt: number
  /** Zeitpunkt der letzten Änderung, Millisekunden seit 1970. */
  updatedAt: number
  text: string
}

/** Eine Ausgabe. */
export interface Expense {
  id: string
  category: string
  /** Betrag in der Währung aus TripMeta. */
  amount: number
  note: string
  stageId: string
  /** ISO-Datum, YYYY-MM-DD. */
  date: string
}

/**
 * Die beiden Checklisten, mit ihren Einträgen und deren Reihenfolge.
 *
 * Früher standen hier nur Wahrheitswerte je Name, während die Einträge
 * unveränderlich in seed.ts lagen. Das ging nicht auf, sobald die Reise von der
 * Planung abweicht: für die Vignette eines Landes, das nicht auf der Liste
 * steht, gab es keinen Platz. Jetzt gehören die Einträge ins Dokument.
 *
 * Die Reihenfolge ist die der Listen und wird vom Nutzer bestimmt. Eine eigene
 * Kennung je Eintrag gibt es bewusst nicht: sie müsste synchronisiert und
 * vergeben werden, ohne dass irgendwer sie bräuchte - verwiesen wird auf diese
 * Einträge von nirgends. Die Oberfläche führt für ihre React-Schlüssel eine
 * eigene Liste mit, siehe features/stages/rowKeys.ts.
 *
 * Das alte Format wird beim Lesen weiterhin verstanden, siehe lib/checks.ts.
 */
export interface Checks {
  vignettes: VignetteCheck[]
  kit: KitCheck[]
}

/** Ein Eintrag der Vignettenliste: eine Vignette, die abgehakt werden kann. */
export interface VignetteCheck extends Vignette {
  done: boolean
}

/** Ein Eintrag der Ersatzteil- und Papierliste. */
export interface KitCheck {
  name: string
  done: boolean
}

/** Ein Foto. Die Bilddaten liegen daneben, hier steht nur die Buchhaltung. */
export interface PhotoMeta {
  id: string
  stageId: string
  mime: string
  size: number
  sha256: string
  createdAt: number
}

/** Eintrag der Vignettenliste. */
export interface Vignette {
  country: string
  price: string
  note: string
}

/** Eine Ausgabenkategorie mit ihrer Farbe in den Diagrammen. */
export interface Category {
  key: string
  color: string
}

/** Dokumentarten des Synchronisationsprotokolls. */
export const KIND = {
  trip: 'trip',
  diary: 'diary',
  /** Ein einzelner Tagebuchbeitrag, siehe DiaryPost. */
  post: 'post',
  expense: 'expense',
  checks: 'checks',
  photo: 'photo',
} as const

/** Feste IDs der Dokumente, von denen es je Nutzer genau eines gibt. */
export const SINGLETON_ID = {
  trip: 'trip',
  checks: 'checks',
} as const
